import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ProfileContext = createContext(null);
const PINK = ["rosa", "pink", "cipria", "salmone", "fucsia", "lilla", "glicine", "lavanda", "pesca", "corallo", "bordeaux"];
const BLUE = ["azzurro", "blu", "celeste", "avio", "navy", "carta da zucchero", "jeans", "petrolio"];
const colorForSex = (product, sex) => {
  const all = product.colors || [];
  const avail = product.colors_available || [];
  const pool = all.filter((c) => !avail.length || avail.includes(c));
  const list = pool.length ? pool : all;
  const kws = sex === "femmina" ? PINK : sex === "maschio" ? BLUE : [];
  return list.find((c) => kws.some((k) => c.toLowerCase().includes(k))) || list[0] || null;
};

export const useProfile = () => useContext(ProfileContext);

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const pid = localStorage.getItem("mb_profile_id");
    if (!pid) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get(`/profile/${pid}`);
      setProfile(data);
      setWishlist(data.wishlist || []);
    } catch (e) {
      localStorage.removeItem("mb_profile_id");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveProfile = useCallback((p) => {
    localStorage.setItem("mb_profile_id", p.id);
    setProfile(p);
    setWishlist(p.wishlist || []);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem("mb_profile_id");
    setProfile(null);
    setWishlist([]);
  }, []);

  const persistWishlist = useCallback(async (items) => {
    setWishlist(items);
    if (!profile) return;
    try {
      await api.put(`/profile/${profile.id}/wishlist`, items);
    } catch (e) {
      toast.error("Impossibile salvare la lista");
    }
  }, [profile]);

  const addToWishlist = useCallback((product) => {
    if (wishlist.find((i) => i.product_id === product.id)) {
      toast("Già nella tua lista 🤍");
      return;
    }
    const item = {
      product_id: product.id,
      title: product.title,
      image: product.image,
      price: product.price,
      url: product.url,
      sizes: product.sizes || [],
      size: (product.sizes && product.sizes[0]) || null,
      colors: product.colors || [],
      colors_available: product.colors_available || [],
      color: colorForSex(product, profile?.sex),
      quantity: 1,
    };
    persistWishlist([...wishlist, item]);
    toast.success("Aggiunto alla tua lista 🤍");
  }, [wishlist, profile?.sex, persistWishlist]);

  const addManyToWishlist = useCallback((products) => {
    const existing = new Set(wishlist.map((i) => i.product_id));
    const additions = [];
    for (const product of products) {
      if (existing.has(product.id)) continue;
      existing.add(product.id);
      additions.push({
        product_id: product.id,
        title: product.title,
        image: product.image,
        price: product.price,
        url: product.url,
        sizes: product.sizes || [],
        size: (product.sizes && product.sizes[0]) || null,
        colors: product.colors || [],
        colors_available: product.colors_available || [],
        color: product.color_chosen && product.color ? product.color : colorForSex(product, profile?.sex),
        color_chosen: !!product.color_chosen,
        quantity: Math.max(1, Number(product.quantity) || 1),
      });
    }
    if (additions.length === 0) {
      toast("Già tutto nella tua lista 🤍");
      return 0;
    }
    persistWishlist([...wishlist, ...additions]);
    toast.success(additions.length === 1 ? "1 capo aggiunto alla tua lista 🤍" : `${additions.length} capi aggiunti alla tua lista 🤍`);
    return additions.length;
  }, [wishlist, profile?.sex, persistWishlist]);

  const updateItem = useCallback((productId, patch) => {
    persistWishlist(wishlist.map((i) => (i.product_id === productId ? { ...i, ...patch } : i)));
  }, [wishlist, persistWishlist]);

  const removeItem = useCallback((productId) => {
    persistWishlist(wishlist.filter((i) => i.product_id !== productId));
  }, [wishlist, persistWishlist]);

  const inWishlist = useCallback((productId) => !!wishlist.find((i) => i.product_id === productId), [wishlist]);

  const updateProfile = useCallback(async (patch) => {
    if (!profile) return;
    const { data } = await api.put(`/profile/${profile.id}`, patch);
    setProfile(data);
    setWishlist(data.wishlist || []);
    return data;
  }, [profile]);

  const value = useMemo(() => ({
    profile, setProfile, saveProfile, refresh, reset, loading, updateProfile,
    wishlist, addToWishlist, addManyToWishlist, updateItem, removeItem, inWishlist,
  }), [profile, saveProfile, refresh, reset, loading, updateProfile,
    wishlist, addToWishlist, addManyToWishlist, updateItem, removeItem, inWishlist]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}
